// Rug Room Shots: "Spend & log" tab. Month (NY) spend cards, a daily image-spend
// bar chart (plain SVG, no chart library), spend by job and by person, and the
// append-only event log with filters and "Load more". Nothing here edits or
// deletes anything: the log is read-only by design.
// AI check (SPEC_AICHECK 4.14 / 5.12): an "AI checks" card, AI checks in the daily tooltip and in the by-job /
// by-person tables (only when the backend sends aiCheckUsd), and the ai_check_* / bulk_approve_* event types.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Button, Alert, CircularProgress, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, IconButton, Collapse, Tooltip, Autocomplete,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MdRefresh, MdExpandMore, MdExpandLess } from 'react-icons/md';
import rrsApi from './api';
import GenerationDetailDialog from './GenerationDetailDialog';
import { AI_COLOR, fmtUsd, fmtInt, errText, fmtNyFull, NyTime, SectionTitle, parseJsonish } from './common';

const EVENT_TYPES = [
  'job_created', 'job_started', 'job_cancelled', 'generation_queued', 'target_style_overridden', 'target_room_overridden',
  'target_excluded', 'target_included', 'generation_regenerate_requested', 'generation_reviewed', 'style_label_added',
  'room_status_changed', 'room_base_pinned', 'setting_changed', 'export_downloaded',
  'worker_started', 'worker_stopping', 'generation_recovered', 'job_planning_started', 'ids_resolved', 'target_planned',
  'target_replanned', 'job_planned', 'job_plan_failed', 'embedding_succeeded', 'embedding_failed', 'generation_claimed',
  'gemini_request_sent', 'gemini_response', 'gemini_retry_scheduled', 'generation_succeeded', 'generation_failed',
  'generation_unknown', 'outputs_spooled', 'outputs_uploaded', 'outputs_upload_failed', 'output_unreadable', 'size_mismatch', 'job_completed',
  'dispatch_blocked', 'dispatch_unblocked', 'backoff_started', 'rooms_discovered', 'room_proposed', 'room_dims_filled',
  'sync_started', 'sync_completed', 'sync_failed', 'cost_rate_mismatch', 'labels_seeded', 'rooms_seeded',
  // Half price (Gemini Batch API, SPEC 9.4-9.8)
  'batch_assembled', 'batch_input_line', 'batch_input_written', 'batch_input_uploaded', 'batch_input_upload_failed',
  'batch_create_sent', 'batch_created', 'batch_create_retry', 'batch_create_rate_limited', 'batch_create_rejected',
  'batch_create_unknown', 'batch_reconcile_listed', 'batch_found_by_listing', 'batch_unknown', 'batch_orphan_found',
  'batch_polled', 'batch_state_changed', 'batch_poll_failed', 'batch_terminal', 'batch_results_downloaded',
  'batch_collect_failed', 'batch_line_collected', 'batch_unknown_key', 'batch_count_mismatch', 'batch_collected',
  'batch_input_file_deleted', 'batch_overdue', 'batch_cancelled_unsent', 'batch_cancel_sent', 'batch_cancel_failed',
  // Page-added rooms and styles, bulk regenerate (SPEC 10)
  'room_added', 'style_added', 'bulk_regenerate_requested', 'bulk_regenerate_ids', 'room_code_conflict',
  // Downloads, zip exports, detail photos, the Outdoor & sunroom list, room-type rules, re-plan (build spec 2.5)
  'download_list_issued', 'export_requested', 'export_reused', 'export_links_issued', 'export_cancel_requested',
  'export_started', 'export_part_uploaded', 'export_ready', 'export_failed', 'export_cancelled', 'export_interrupted',
  'detail_photo_uploaded', 'details_refreshed', 'scene_rule_saved', 'scene_rule_removed', 'room_rules_saved',
  'room_rules_reset', 'room_rules_applied', 'job_replanned', 'live_room_check_unavailable',
  // AI check and Approve AI-passed images (SPEC_AICHECK 1.6)
  'ai_check_queued', 'ai_checks_requested', 'ai_checks_cancelled', 'ai_check_sent', 'ai_check_retry_scheduled',
  'ai_check_backoff_started', 'ai_check_done', 'ai_check_failed', 'ai_check_recovered', 'ai_check_blocked',
  'ai_check_unblocked', 'bulk_approve_requested', 'bulk_approve_ids',
];

const nyMonthNow = () => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  const p = (t) => (parts.find((x) => x.type === t) || {}).value;
  return `${p('year')}-${p('month')}`;
};
const monthLabel = (m) => {
  const [y, mo] = String(m).split('-').map(Number);
  if (!y || !mo) return m;
  return new Date(Date.UTC(y, mo - 1, 15)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

// Short, human summary of an event payload (the full JSON is one click away).
const payloadSummary = (p) => {
  if (!p || typeof p !== 'object') return '';
  const keys = ['decision', 'verdict', 'score', 'reason', 'reasonCode', 'old', 'new', 'status', 'speed', 'batch_id', 'state', 'count', 'queued', 'est_usd',
    'estUsd', 'usd', 'error_code', 'errorCode', 'http_status', 'httpStatus', 'latency_ms', 'note', 'roomCode', 'room_code', 'resolved',
    'unresolved', 'checkId', 'check_ref', 'approve_ref'];
  const parts = [];
  keys.forEach((k) => {
    if (p[k] === undefined || p[k] === null || p[k] === '') return;
    let v = p[k];
    if (Array.isArray(v)) v = `${v.length} item(s)`;
    else if (typeof v === 'object') v = JSON.stringify(v);
    parts.push(`${k}: ${String(v).slice(0, 60)}`);
  });
  if (!parts.length) {
    Object.keys(p).slice(0, 3).forEach((k) => {
      const v = p[k];
      parts.push(`${k}: ${typeof v === 'object' ? JSON.stringify(v).slice(0, 40) : String(v).slice(0, 40)}`);
    });
  }
  return parts.slice(0, 4).join(' · ');
};

// ---------------------------------------------------------------------------
// Image spend per day; when the backend splits it (fastUsd / batchUsd), each bar is
// stacked: Fast at the bottom in the tool colour, Half price on top in a lighter tint.
const BATCH_BAR = '#B39DDB';
const DailyBars = ({ byDay }) => {
  const theme = useTheme();
  const days = byDay || [];
  if (!days.length) return <Typography variant="body2" color="text.secondary">No spend this month.</Typography>;
  const split = days.some((d) => d.fastUsd !== undefined || d.batchUsd !== undefined);
  const max = Math.max(...days.map((d) => Number(d.imageUsd) || 0), 0.01);
  const W = Math.max(days.length * 26, 300);
  const H = 160;
  const barW = Math.min(20, (W / days.length) - 6);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg width={W} height={H + 28} role="img" aria-label="Daily image spend">
        <line x1="0" y1={H} x2={W} y2={H} stroke={theme.palette.divider} />
        {days.map((d, i) => {
          const v = Number(d.imageUsd) || 0;
          const h = Math.max(v > 0 ? 2 : 0, (v / max) * (H - 18));
          const x = i * (W / days.length) + ((W / days.length) - barW) / 2;
          const vb = split ? Number(d.batchUsd) || 0 : 0;
          const hb = v > 0 ? (vb / v) * h : 0;
          const parts = split ? ` (Fast ${fmtUsd(d.fastUsd || 0)}, Half price ${fmtUsd(vb)})` : '';
          return (
            <g key={d.date}>
              <title>{`${d.date}: ${fmtUsd(v)}${parts} on ${fmtInt(d.images)} image(s)${d.embeddingUsd ? `, embeddings ${fmtUsd(d.embeddingUsd, 4)}` : ''}${Number(d.aiCheckUsd) > 0 ? `, AI checks ${fmtUsd(d.aiCheckUsd)}` : ''}`}</title>
              <rect x={x} y={H - h} width={barW} height={h} rx="3" fill={AI_COLOR} opacity="0.85" />
              {hb > 0 && <rect x={x} y={H - h} width={barW} height={hb} rx="3" fill={BATCH_BAR} />}
              <text x={x + barW / 2} y={H + 14} textAnchor="middle" fontSize="10" fill={theme.palette.text.secondary}>
                {String(d.date).slice(8, 10)}
              </text>
            </g>
          );
        })}
        <text x="2" y="10" fontSize="10" fill={theme.palette.text.secondary}>{fmtUsd(max)}</text>
      </svg>
      {split && (
        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: AI_COLOR, opacity: 0.85 }} />
            <Typography variant="caption" color="text.secondary">Fast</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: BATCH_BAR }} />
            <Typography variant="caption" color="text.secondary">Half price</Typography>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

const StatCard = ({ label, value, sub, color }) => (
  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, flex: 1, minWidth: 160 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

// ---------------------------------------------------------------------------
const LogRow = ({ ev: raw, onOpenGen }) => {
  const [open, setOpen] = useState(false);
  const ev = { ...raw, payload: parseJsonish(raw.payload) };
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}><NyTime ts={ev.ts} /></TableCell>
        <TableCell sx={{ maxWidth: 200 }}>
          <Typography variant="body2" noWrap title={ev.actorEmail || ev.actorWorkerId || ev.actorType}>
            {ev.actorEmail || (ev.actorType === 'worker' ? 'worker' : ev.actorType)}
          </Typography>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{ev.eventType}</TableCell>
        <TableCell>{ev.jobId ? `#${ev.jobId}` : ''}</TableCell>
        <TableCell>{ev.targetId || ''}{ev.webId ? <Typography variant="caption" color="text.secondary" display="block">{ev.webId}</Typography> : null}</TableCell>
        <TableCell>
          {ev.genId ? <Button size="small" sx={{ minWidth: 0, p: 0 }} onClick={() => onOpenGen(ev.genId)}>{ev.genId}</Button> : ''}
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{ev.roomCode || ''}</TableCell>
        <TableCell sx={{ maxWidth: 360 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {payloadSummary(ev.payload)}
          </Typography>
        </TableCell>
        <TableCell padding="checkbox">
          {ev.payload ? <IconButton size="small" onClick={() => setOpen((o) => !o)}>{open ? <MdExpandLess /> : <MdExpandMore />}</IconButton> : null}
        </TableCell>
      </TableRow>
      {ev.payload ? (
        <TableRow>
          <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
            <Collapse in={open} unmountOnExit>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Event {ev.eventId} · {fmtNyFull(ev.ts)}{ev.actorWorkerId ? ` · ${ev.actorWorkerId}` : ''}
              </Typography>
              <Box component="pre" sx={{ m: 0, mb: 1, p: 1, fontSize: 12, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto' }}>
                {JSON.stringify(ev.payload, null, 2)}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

const SpendLogTab = ({ config }) => {
  const [month, setMonth] = useState(nyMonthNow());
  const [spend, setSpend] = useState(null);
  const [spendError, setSpendError] = useState('');
  const [spendLoading, setSpendLoading] = useState(false);

  const [events, setEvents] = useState([]);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');
  const [fType, setFType] = useState('');
  const [fJob, setFJob] = useState('');
  const [fGen, setFGen] = useState('');
  const [fUser, setFUser] = useState('');
  const [applied, setApplied] = useState({ type: '', jobId: '', genId: '', userNo: '' });
  const [detailGen, setDetailGen] = useState(null);

  const loadSpend = useCallback(async () => {
    setSpendLoading(true);
    try {
      setSpend(await rrsApi.spend(month));
      setSpendError('');
    } catch (e) {
      setSpendError(errText(e));
    } finally {
      setSpendLoading(false);
    }
  }, [month]);

  const loadEvents = useCallback(async (beforeId) => {
    setLogLoading(true);
    try {
      const res = await rrsApi.events({ ...applied, beforeId: beforeId || '', limit: 100 });
      const items = res.items || [];
      setEvents((cur) => (beforeId ? [...cur, ...items] : items));
      // A short page means the end of the log.
      setNextBeforeId(items.length >= 100 ? (res.nextBeforeId || items[items.length - 1].eventId) : null);
      setLogError('');
    } catch (e) {
      setLogError(errText(e));
    } finally {
      setLogLoading(false);
    }
  }, [applied]);

  useEffect(() => { loadSpend(); }, [loadSpend]);
  useEffect(() => { loadEvents(null); }, [loadEvents]);

  const months = useMemo(() => {
    const list = (spend && spend.months) || [];
    return list.includes(month) ? list : [month, ...list];
  }, [spend, month]);

  const applyFilters = () => setApplied({
    type: fType || '', jobId: fJob.trim(), genId: fGen.trim(), userNo: fUser.trim(),
  });
  const clearFilters = () => { setFType(''); setFJob(''); setFGen(''); setFUser(''); setApplied({ type: '', jobId: '', genId: '', userNo: '' }); };

  const s = spend || {};
  // AI checks only with a backend that reports them (SPEC_AICHECK 4.14).
  const aiSpend = s.aiCheckUsd !== undefined && s.aiCheckUsd !== null;
  const aiEnabled = config && config.features ? config.features.aiCheck === true : undefined;

  return (
    <Box>
      {/* Spend */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <TextField select size="small" label="Month (New York)" value={month} sx={{ minWidth: 200 }} onChange={(e) => setMonth(e.target.value)}>
          {months.map((m) => <MenuItem key={m} value={m}>{monthLabel(m)}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        {spendLoading && <CircularProgress size={20} />}
        <Button startIcon={<MdRefresh />} onClick={() => { loadSpend(); loadEvents(null); }}>Refresh</Button>
      </Stack>
      {spendError && <Alert severity="error" sx={{ mb: 1.5 }}>{spendError}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <StatCard
          label={`Total, ${monthLabel(month)}`} value={fmtUsd(s.totalUsd || 0)} color={AI_COLOR}
          sub={aiSpend ? 'images + embeddings + AI checks, billed outcomes only' : 'images + embeddings, billed outcomes only'}
        />
        {s.imageUsdFast !== undefined || s.imageUsdBatch !== undefined ? (
          <>
            <StatCard label="Images · Fast" value={fmtUsd(s.imageUsdFast || 0)} sub={`${fmtInt(s.imagesFast || 0)} image(s), real time`} />
            <StatCard label="Images · Half price" value={fmtUsd(s.imageUsdBatch || 0)} sub={`${fmtInt(s.imagesBatch || 0)} image(s), Batch API`} />
          </>
        ) : (
          <StatCard label="Images" value={fmtUsd(s.imageUsd || 0)} sub={`${fmtInt(s.images || 0)} image(s)`} />
        )}
        <StatCard label="Embeddings" value={fmtUsd(s.embeddingUsd || 0, 4)} sub={`${fmtInt(s.embeddings || 0)} silo embedding(s)`} />
        {aiSpend && (
          <StatCard label="AI checks" value={fmtUsd(s.aiCheckUsd || 0)} sub={`${fmtInt(s.aiChecks || 0)} check(s)`} />
        )}
        <Tooltip title="Calls whose outcome is unknown (timed out after sending, or a half-price image Google never returned). Google may or may not have billed them; not included in the total.">
          <Box sx={{ flex: 1, display: 'flex', minWidth: 160 }}>
            <StatCard label="Possible (unknown outcomes)" value={fmtUsd(s.possibleUsd || 0)} sub="shown separately" color={s.possibleUsd ? 'warning.main' : undefined} />
          </Box>
        </Tooltip>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <SectionTitle>Daily image spend</SectionTitle>
        <DailyBars byDay={s.byDay} />
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 0 }}>
          <SectionTitle>By job</SectionTitle>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell><TableCell align="right">Images</TableCell>
                {aiSpend && <TableCell align="right">AI checks</TableCell>}
                <TableCell align="right">USD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(s.byJob || []).length === 0 && <TableRow><TableCell colSpan={aiSpend ? 4 : 3} sx={{ color: 'text.secondary' }}>None</TableCell></TableRow>}
              {(s.byJob || []).map((j) => (
                <TableRow key={j.jobId}>
                  <TableCell><Typography variant="body2" noWrap title={j.name}>#{j.jobId} · {j.name}</Typography></TableCell>
                  <TableCell align="right">{fmtInt(j.images)}</TableCell>
                  {aiSpend && <TableCell align="right">{fmtInt(j.aiChecks || 0)}</TableCell>}
                  <TableCell align="right">{fmtUsd(j.usd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 0 }}>
          <SectionTitle>By person</SectionTitle>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell><TableCell align="right">Images</TableCell>
                {aiSpend && <TableCell align="right">AI checks</TableCell>}
                <TableCell align="right">USD</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(s.byUser || []).length === 0 && <TableRow><TableCell colSpan={aiSpend ? 4 : 3} sx={{ color: 'text.secondary' }}>None</TableCell></TableRow>}
              {(s.byUser || []).map((u) => (
                <TableRow key={u.email}>
                  <TableCell><Typography variant="body2" noWrap title={u.email}>{u.email}</Typography></TableCell>
                  <TableCell align="right">{fmtInt(u.images)}</TableCell>
                  {aiSpend && <TableCell align="right">{fmtInt(u.aiChecks || 0)}</TableCell>}
                  <TableCell align="right">{fmtUsd(u.usd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Stack>

      {/* Log */}
      <SectionTitle>Log</SectionTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Every input, decision, override, Gemini call, S3 write, review and error, newest first. The log is append-only: nothing can be edited or deleted.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }} alignItems={{ md: 'center' }}>
        <Autocomplete
          size="small" options={EVENT_TYPES} value={fType || null} onChange={(e, v) => setFType(v || '')}
          renderInput={(params) => <TextField {...params} label="Event type" />} sx={{ minWidth: 260 }}
        />
        <TextField size="small" label="Job #" value={fJob} onChange={(e) => setFJob(e.target.value.replace(/[^0-9]/g, ''))} sx={{ width: 110 }} />
        <TextField size="small" label="Gen id" value={fGen} onChange={(e) => setFGen(e.target.value.replace(/[^0-9]/g, ''))} sx={{ width: 120 }} />
        <TextField size="small" label="User no" value={fUser} onChange={(e) => setFUser(e.target.value.replace(/[^0-9]/g, ''))} sx={{ width: 110 }} />
        <Button variant="outlined" onClick={applyFilters}>Apply</Button>
        <Button onClick={clearFilters}>Clear</Button>
        {logLoading && <CircularProgress size={20} />}
      </Stack>
      {logError && <Alert severity="error" sx={{ mb: 1.5 }}>{logError}</Alert>}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time (NY)</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Job</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Gen</TableCell>
              <TableCell>Room</TableCell>
              <TableCell>Details</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 && !logLoading && (
              <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No events.</TableCell></TableRow>
            )}
            {events.map((ev) => <LogRow key={ev.eventId} ev={ev} onOpenGen={setDetailGen} />)}
          </TableBody>
        </Table>
      </TableContainer>
      {nextBeforeId && (
        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
          <Button variant="outlined" onClick={() => loadEvents(nextBeforeId)} disabled={logLoading}>Load more</Button>
        </Box>
      )}

      <GenerationDetailDialog genId={detailGen} onClose={() => setDetailGen(null)} aiEnabled={aiEnabled} />
    </Box>
  );
};

export default SpendLogTab;
